import {Component, EventEmitter, inject, Input, Output, ChangeDetectionStrategy} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {
  faArrowUpFromBracket,
  faBolt,
  faCircleInfo,
  faClock,
  faHandPointer,
  faSparkles,
  faSpinner
} from '@fortawesome/pro-solid-svg-icons';
import {MyCheckBuilderState, TriggerMode} from '../my-checks-builder.model';
import {
  BicInputComponent,
  BicTextareaComponent,
  BicToastService,
  TooltipDirective
} from '@buildinginformationcloud/bic-ui-components';
import {WorkflowService} from '../../../../services/workflow.service';

@Component({
  selector: 'my-checks-builder-start-step',
  standalone: true,
  imports: [FormsModule, FaIconComponent, TooltipDirective, BicInputComponent, BicTextareaComponent],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './builder-start-step.component.html',
})
export class BuilderStartStepComponent {
  @Input() state!: MyCheckBuilderState;
  /** Zeigt Pflichtfeld-Hinweise an (nach erstem Speicherversuch). */
  @Input() showErrors = false;
  @Output() stateChange = new EventEmitter<Partial<MyCheckBuilderState>>();
  /**
   * Bestehender Zeitplan im aktuellen Projekt als Text („Täglich um 08:00 Uhr · nächste Ausführung
   * …“), sonst {@code null}.
   */
  @Input() scheduleSummary: string | null = null;
  /** Warum die Kachel „Zeitplan“ gesperrt ist, oder {@code null}, wenn sie klickbar ist. */
  @Input() scheduleUnavailableReason: string | null = null;
  /** Ungespeicherte Änderungen: ein Klick auf „Zeitplan“ speichert zuerst (siehe Builder). */
  @Input() scheduleSavesFirst = false;
  /**
   * Öffnet das Zeitplan-Popup. Der Trigger des Manifests bleibt `manual_trigger`: ein Zeitplan
   * gehört zum Paar Prüflauf × Projekt und liegt nicht im Manifest.
   */
  @Output() openSchedule = new EventEmitter<void>();

  faSparkles = faSparkles;
  faSpinner = faSpinner;
  faCircleInfo = faCircleInfo;
  optimizingDescription = false;
  previousDescription: string | null = null;
  private workflowService = inject(WorkflowService);
  private toastService = inject(BicToastService);

  private readonly descriptionOptimizationContext =
    'Es handelt sich um die Beschreibung eines myChecks: Sie beschreibt, was geprüft werden soll, wird '
    + 'in der Übersicht angezeigt und dient chatBIC als Grundlage für den Check.';
  private readonly descriptionOptimizationSystemPrompt =
    'Du bist ein Assistent, der Beschreibungen von myChecks klarer, präziser und vollständiger '
    + 'formuliert. Gib nur den optimierten Text als reinen Text zurück. Wiederhole niemals den '
    + 'Ausgangstext und verwende keinerlei Markdown.';

  triggerOptions: { mode: TriggerMode; label: string; description: string; icon: any }[] = [
    {mode: 'manual', label: 'Manuell', description: 'Per Klick gestartet', icon: faHandPointer},
    {mode: 'fileUpload', label: 'Dateiupload', description: 'Bei neuer Datei', icon: faArrowUpFromBracket},
    {mode: 'schedule', label: 'Zeitplan', description: 'Einmalig oder wiederkehrend', icon: faClock},
    {mode: 'webhook', label: 'Webhook', description: 'Bei Plattform-Event', icon: faBolt},
  ];

  selectTrigger(mode: TriggerMode): void {
    this.stateChange.emit({triggerMode: mode});
  }

  onTriggerClick(mode: TriggerMode): void {
    if (mode === 'manual') {
      this.selectTrigger(mode);
    } else if (mode === 'schedule' && this.scheduleUnavailableReason === null) {
      this.openSchedule.emit();
    }
  }

  isTriggerEnabled(mode: TriggerMode): boolean {
    return mode === 'manual' || (mode === 'schedule' && this.scheduleUnavailableReason === null);
  }

  /** Tooltip der Kachel; `undefined` statt '' (die Tooltip-Direktive zeigt sonst einen leeren Pfeil). */
  triggerTooltip(mode: TriggerMode): string | undefined {
    if (mode === 'manual') return undefined;
    if (mode === 'schedule') {
      if (this.scheduleUnavailableReason !== null) return this.scheduleUnavailableReason;
      return this.scheduleSavesFirst ? 'Speichert zuerst die Änderungen am Prüflauf und öffnet dann den Zeitplan.' : undefined;
    }
    return 'Demnächst verfügbar';
  }

  /** Klassen der Kachel als ein String (kein ngClass-Objekt, siehe Memory „ngClass overlapping keys“). */
  triggerClass(mode: TriggerMode): string {
    const highlighted = this.state.triggerMode === mode || (mode === 'schedule' && !!this.scheduleSummary);
    if (highlighted) return 'border-gray-500 bg-gray-100';
    return this.isTriggerEnabled(mode)
      ? 'border-gray-200 bg-white hover:border-gray-300'
      : 'border-gray-200 bg-white opacity-50 cursor-not-allowed';
  }

  optimizeDescription(): void {
    const prompt = this.state.description?.trim();
    if (!prompt || this.optimizingDescription) return;

    this.optimizingDescription = true;
    this.workflowService.optimizePrompt(prompt, {
      context: this.descriptionOptimizationContext,
      systemPrompt: this.descriptionOptimizationSystemPrompt,
    }).subscribe({
      next: (optimized) => {
        this.previousDescription = this.state.description;
        this.stateChange.emit({description: optimized});
        this.optimizingDescription = false;
      },
      error: () => {
        this.optimizingDescription = false;
        this.toastService.error('Beschreibung konnte nicht verbessert werden');
      },
    });
  }

  restoreDescription(): void {
    if (this.previousDescription == null) return;
    this.stateChange.emit({description: this.previousDescription});
    this.previousDescription = null;
  }
}
